import { Col, Collapse, Row } from "antd";
import type { CollapseProps } from "antd";
import CollapseExpandIcon from "../CollapseExpandIcon";
import SwitchOptionComponent from "../SwitchComponent";
import InputNumberComponent from "../InputNumberComponent";
import InputComponent from "../InputComponent";
import TextareaComponent from "../TextareaComponent";
import FileUploadComponent, { FileData } from "../FileUploadComponent";
import { Culture } from "../../context/VolunteersHighlightContext/constants";
import { VolunteersExperienceRules } from "../../context/VolunteersExperienceContext/constants";
import {
	GENERAL_SECTION_KEY,
	getDetailSectionKey,
	VolunteersExperienceDetailTexts,
	VolunteersExperienceFormKeys,
	VolunteersExperienceGeneralTexts,
	VolunteersExperiencePictureUpload,
	VolunteersExperienceSectionLabels,
} from "./constants";

export interface VolunteersExperienceGeneralValues {
	isActive: boolean;
	displayOrder?: number;
	name: string;
	surname: string;
	picture: File | null;
	// The stored picture of the update form.
	pictureUrl?: string;
}

export interface VolunteersExperienceLocalizableValues {
	culture: Culture;
	description: string;
	title: string;
	company: string;
}

export type VolunteersExperienceLocalizableChanges = Partial<Pick<VolunteersExperienceLocalizableValues, "description" | "title" | "company">>;

export const applyExperienceLocalizableChanges = <T extends { culture: Culture }>(
	localizables: T[],
	culture: Culture,
	changes: VolunteersExperienceLocalizableChanges
): T[] => localizables.map(item => (item.culture === culture ? { ...item, ...changes } : item));

interface VolunteersExperienceFormProps {
	general: VolunteersExperienceGeneralValues;
	localizables: VolunteersExperienceLocalizableValues[];
	// On the add form the name, the surname and both experience texts are required; the update form only sends what
	// changed.
	isRequired: boolean;
	onGeneralChange: (changes: Partial<VolunteersExperienceGeneralValues>) => void;
	onLocalizableChange: (culture: Culture, changes: VolunteersExperienceLocalizableChanges) => void;
}

// "Genel Bilgiler" with the status, the order, the volunteer's name and picture, then one "Gönüllü Deneyimi Detayı -
// <language>" section per culture with the title, the company and the experience text.
const VolunteersExperienceForm = ({ general, localizables, isRequired, onGeneralChange, onLocalizableChange }: VolunteersExperienceFormProps) => {
	const items: CollapseProps["items"] = [
		{
			key: GENERAL_SECTION_KEY,
			label: VolunteersExperienceSectionLabels.GENERAL,
			children: (
				<>
					<Row gutter={[20, 16]}>
						<Col xs={24} sm={12} md={8} lg={6}>
							<SwitchOptionComponent
								title={VolunteersExperienceGeneralTexts.isActive.label}
								helperText={VolunteersExperienceGeneralTexts.isActive.helperText}
								value={general.isActive}
								onChange={checked => onGeneralChange({ isActive: checked })}
							/>
						</Col>
					</Row>
					<Row gutter={[20, 16]}>
						<Col xs={24} sm={12} md={6} lg={4}>
							<InputNumberComponent
								title={VolunteersExperienceGeneralTexts.displayOrder.label}
								positiveOnly
								value={general.displayOrder}
								placeholder={VolunteersExperienceGeneralTexts.displayOrder.placeholder}
								onChange={value => onGeneralChange({ displayOrder: value === null || value === "" ? undefined : Number(value) })}
							/>
						</Col>
					</Row>
					<Row gutter={[20, 20]}>
						<Col xs={24} sm={24} md={12} lg={12}>
							<InputComponent
								title={VolunteersExperienceGeneralTexts.name.label}
								isRequired={isRequired}
								placeholder={VolunteersExperienceGeneralTexts.name.placeholder}
								value={general.name}
								maxLength={VolunteersExperienceRules.nameMaxLength}
								onChange={e => onGeneralChange({ name: e.target.value })}
							/>
							<InputComponent
								title={VolunteersExperienceGeneralTexts.surname.label}
								isRequired={isRequired}
								placeholder={VolunteersExperienceGeneralTexts.surname.placeholder}
								value={general.surname}
								maxLength={VolunteersExperienceRules.surnameMaxLength}
								onChange={e => onGeneralChange({ surname: e.target.value })}
							/>
						</Col>
						<Col xs={24} sm={24} md={12} lg={12}>
							<FileUploadComponent
								title={VolunteersExperienceGeneralTexts.picture.label}
								maxFileSize={VolunteersExperiencePictureUpload.maxFileSize}
								allowedExtensions={VolunteersExperiencePictureUpload.extensions}
								defaultUrl={general.pictureUrl || undefined}
								onChange={(files: FileData[] | null) => onGeneralChange({ picture: files?.[0]?.file ?? null })}
							/>
						</Col>
					</Row>
				</>
			),
		},
		...localizables.map(localizable => {
			const texts = VolunteersExperienceDetailTexts[localizable.culture];

			return {
				key: getDetailSectionKey(localizable.culture),
				label: VolunteersExperienceSectionLabels.getDetail(localizable.culture),
				children: (
					<Row gutter={[20, 16]}>
						<Col xs={24} sm={24} md={12} lg={12}>
							<InputComponent
								title={texts.title.label}
								placeholder={texts.title.placeholder}
								value={localizable.title}
								maxLength={VolunteersExperienceRules.titleMaxLength}
								onChange={e => onLocalizableChange(localizable.culture, { title: e.target.value })}
							/>
						</Col>
						<Col xs={24} sm={24} md={12} lg={12}>
							<InputComponent
								title={texts.company.label}
								placeholder={texts.company.placeholder}
								value={localizable.company}
								maxLength={VolunteersExperienceRules.companyMaxLength}
								onChange={e => onLocalizableChange(localizable.culture, { company: e.target.value })}
							/>
						</Col>
						<Col span={24}>
							<TextareaComponent
								title={texts.description.label}
								isRequired={isRequired}
								placeholder={texts.description.placeholder}
								rows={8}
								resizable={false}
								limitCharacter={VolunteersExperienceRules.descriptionMaxLength}
								lineBreaks="crlf"
								value={localizable.description}
								onChange={e => onLocalizableChange(localizable.culture, { description: e.target.value })}
							/>
						</Col>
					</Row>
				),
			};
		}),
	];

	return (
		<Collapse
			items={items}
			expandIconPosition="end"
			bordered={false}
			ghost
			expandIcon={CollapseExpandIcon}
			defaultActiveKey={VolunteersExperienceFormKeys}
		/>
	);
};

export default VolunteersExperienceForm;
